import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Sidebar } from '@/components/layout/Sidebar';
import { useAuth } from '@/lib/auth';
import DeckGL from '@deck.gl/react';
import { ArcLayer, BitmapLayer, ScatterplotLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
import { searchBoundaries } from '@/connectors/ogc';
import { fetchOdinSpider, fetchOdinSpiderProfile } from '@/services/odin-api';
import type { BoundarySearchResult, SpiderResponse, SpiderProfileResponse } from '@/types/odin';

// PDOK BRT achtergrondkaart (grijs) — keyless, EPSG:3857, 256 px, z0-19.
// Was CARTO light_all: CARTO switched on API-key enforcement for its raster
// (PNG) basemaps, and unkeyed tiles come back HTTP 200 with "API KEY REQUIRED"
// baked into the image — so monitoring stays green while every map is visibly
// broken. Single host, so no a/b/c round-robin here. Same swap as sb-nrm-toolkit.
const BASEMAP_URLS =
  'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png';

const INITIAL_VIEW = {
  longitude: 5.2913,
  latitude: 52.1326,
  zoom: 7,
  pitch: 30,
  bearing: 0,
};

const TEAL: [number, number, number] = [6, 138, 161];
const PINK: [number, number, number] = [230, 46, 115];

// Values MUST match the odin-spider process vocabulary exactly (sb-api
// app/processes/odin_spider_process.py VALID_MODES / VALID_MOTIVES — they name
// the pre-aggregated flow_{mode}_{motive} columns in mobiliteit_odin_v2).
// Empty value = filter omitted -> process defaults (AllModes/AllMotives).
// The previous lists used the ODIN-trips Dutch vocabulary, so every
// non-default filter returned InvalidParameterValue (Steven, 2026-07-02).
const MODE_OPTIONS = [
  { value: '', label: 'Alle vervoerswijzen' },
  { value: 'Auto', label: 'Auto' },
  { value: 'OV', label: 'OV (totaal)' },
  { value: 'Btm', label: 'Bus/Tram/Metro' },
  { value: 'Trein', label: 'Trein' },
  { value: 'Fiets', label: 'Fiets' },
  { value: 'Lopen', label: 'Lopen' },
  { value: 'Overig', label: 'Overig' },
];

const MOTIVE_OPTIONS = [
  { value: '', label: 'Alle motieven' },
  { value: 'ToWork', label: 'Werk' },
  { value: 'ToHome', label: 'Naar huis' },
  { value: 'Business', label: 'Zakelijk' },
  { value: 'ToEducation', label: 'Onderwijs' },
  { value: 'Shopping', label: 'Winkelen/boodschappen' },
  { value: 'Visiting', label: 'Bezoek' },
  { value: 'SportOrHobby', label: 'Sport/hobby' },
  { value: 'PickUpPeople', label: 'Personen brengen/halen' },
  { value: 'PickUpGoods', label: 'Goederen halen/brengen' },
  { value: 'PersonalCare', label: 'Persoonlijke verzorging/diensten' },
  { value: 'TransportAsJob', label: 'Beroepsmatig vervoer' },
  { value: 'TouringOrWalking', label: 'Toeren/wandelen' },
  { value: 'OtherSpareTime', label: 'Overige vrije tijd' },
  { value: 'Other_NFD', label: 'Overig' },
];

type SpiderArc = {
  sourcePosition: [number, number];
  targetPosition: [number, number];
  flow: number;
  origin: string;
  destination: string;
};

export default function Spinnen() {
  const { user } = useAuth();
   
  const deckRef = useRef<any>(null);

  // --- Search state ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<BoundarySearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedGemeente, setSelectedGemeente] = useState<{ name: string; code: string } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // --- Controls ---
  const [mode, setMode] = useState('');
  const [motive, setMotive] = useState('');
  const [topN, setTopN] = useState(25);
  const [includeInternal, setIncludeInternal] = useState(false);

  // --- Data ---
  const [spiderData, setSpiderData] = useState<SpiderResponse | null>(null);
  const [profileData, setProfileData] = useState<SpiderProfileResponse | null>(null);
  const [showHomeLocations, setShowHomeLocations] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Map view ---
  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [fullscreen, setFullscreen] = useState(false);

  // --- Debounced gemeente search ---
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const results = await searchBoundaries(value, 'gemeente', 10);
      setSearchResults(results);
      setSearchOpen(results.length > 0);
    }, 300);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSelectGemeente = useCallback((result: BoundarySearchResult) => {
    setSelectedGemeente({ name: result.name, code: result.code });
    setSearchQuery(result.name);
    setSearchOpen(false);
    setSearchResults([]);
  }, []);

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    if (!selectedGemeente) return;
    setLoading(true);
    setError(null);
    setSpiderData(null);
    setProfileData(null);

    const [result, profileResult] = await Promise.all([
      fetchOdinSpider({
        gemeente: selectedGemeente.name,
        mode: mode || undefined,
        motive: motive || undefined,
        top_n: topN,
        include_internal: includeInternal,
      }),
      fetchOdinSpiderProfile({
        gemeente: selectedGemeente.name,
        mode: mode || undefined,
        motive: motive || undefined,
      }),
    ]);

    setLoading(false);

    if ('data' in profileResult) {
      setProfileData(profileResult.data);
    }

    if ('error' in result) {
      setError(result.error);
    } else {
      setSpiderData(result.data);
      // Zoom to fit arcs if we have data
      if (result.data.features.length > 0) {
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
        for (const f of result.data.features) {
          const props = f.properties;
          minLng = Math.min(minLng, props.lon_a, props.lon_b);
          minLat = Math.min(minLat, props.lat_a, props.lat_b);
          maxLng = Math.max(maxLng, props.lon_a, props.lon_b);
          maxLat = Math.max(maxLat, props.lat_a, props.lat_b);
        }
        setViewState((prev) => ({
          ...prev,
          longitude: (minLng + maxLng) / 2,
          latitude: (minLat + maxLat) / 2,
          zoom: 8,
          pitch: 30,
          bearing: 0,
        }));
      }
    }
  }, [selectedGemeente, mode, motive, topN, includeInternal]);

  // --- Arcs data ---
  const arcs: SpiderArc[] = useMemo(() => {
    if (!spiderData) return [];
    return spiderData.features.map((f) => {
      const p = f.properties;
      return {
        sourcePosition: [p.lon_a, p.lat_a] as [number, number],
        targetPosition: [p.lon_b, p.lat_b] as [number, number],
        flow: p.flow,
        origin: p.origin,
        destination: p.destination,
      };
    });
  }, [spiderData]);

  const maxFlow = useMemo(() => {
    if (arcs.length === 0) return 1;
    return Math.max(...arcs.map((a) => a.flow));
  }, [arcs]);

  const totalFlow = useMemo(() => {
    return arcs.reduce((sum, a) => sum + a.flow, 0);
  }, [arcs]);

  // --- Deck layers ---
  const layers = useMemo(() => {
     
    const basemap = new TileLayer<any>({
      id: 'basemap',
      data: BASEMAP_URLS,
      tileSize: 256,
      maxZoom: 19,
       
      renderSubLayers: (props: any) => {
        const { west, south, east, north } = props.tile.bbox;
        return new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: [west, south, east, north],
        });
      },
    });

    const result: any[] = [basemap];

    if (arcs.length > 0) {
      result.push(
        new ArcLayer<SpiderArc>({
          id: 'spider-arcs',
          data: arcs,
          getSourcePosition: (d) => d.sourcePosition,
          getTargetPosition: (d) => d.targetPosition,
          getSourceColor: TEAL,
          getTargetColor: PINK,
          getWidth: (d) => Math.max(1, (d.flow / maxFlow) * 10),
          pickable: true,
          autoHighlight: true,
          highlightColor: [255, 200, 0, 180],
        }),
      );
    }

    // Home locations scatter
    if (showHomeLocations && profileData?.home_locations) {
      const homeData = profileData.home_locations.filter((h) => h.lat != null && h.lng != null);
      if (homeData.length > 0) {
        const maxCount = Math.max(...homeData.map((h) => h.count));
        result.push(
          new ScatterplotLayer({
            id: 'home-locations',
            data: homeData,
            getPosition: (d: (typeof homeData)[0]) => [d.lng!, d.lat!],
            getRadius: (d: (typeof homeData)[0]) => 800 + (d.count / maxCount) * 4000,
            getFillColor: [6, 138, 161, 140],
            getLineColor: [6, 138, 161, 220],
            lineWidthMinPixels: 1,
            stroked: true,
            pickable: true,
            radiusUnits: 'meters',
            autoHighlight: true,
            highlightColor: [6, 138, 161, 200],
          }),
        );
      }
    }

    return result;
  }, [arcs, maxFlow, showHomeLocations, profileData]);

  // --- Export PNG ---
  const handleExportPNG = useCallback(() => {
    const deck = (deckRef.current as any)?.deck;
    if (!deck) return;
    const canvas = deck.getCanvas() as HTMLCanvasElement | null;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `spinnenkaart-${selectedGemeente?.name ?? 'export'}.png`;
    a.click();
  }, [selectedGemeente]);

  // --- Export SVG ---
  const handleExportSVG = useCallback(() => {
    const deck = (deckRef.current as any)?.deck;
    if (!deck || arcs.length === 0) return;

    const viewport = deck.getViewports()[0];
    if (!viewport) return;

    const width = viewport.width;
    const height = viewport.height;

    const paths = arcs.map((arc) => {
      const [sx, sy] = viewport.project(arc.sourcePosition);
      const [tx, ty] = viewport.project(arc.targetPosition);
      // Quadratic bezier with control point above midpoint
      const cx = (sx + tx) / 2;
      const cy = Math.min(sy, ty) - Math.abs(tx - sx) * 0.2;
      const w = 1 + (arc.flow / maxFlow) * 7;
      return `<path d="M${sx},${sy} Q${cx},${cy} ${tx},${ty}" fill="none" stroke="#068AA1" stroke-width="${w.toFixed(1)}" opacity="0.7"/>`;
    });

    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#f8f9fa"/>
  ${paths.join('\n  ')}
</svg>`;

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spinnenkaart-${selectedGemeente?.name ?? 'export'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [arcs, maxFlow, selectedGemeente]);

  // --- Tooltip ---
   
  const getTooltip = useCallback(({ object, layer }: { object?: any; layer?: any }) => {
    if (!object) return null;
    if (layer?.id === 'home-locations') {
      return {
        html: `<div style="padding:6px 10px;font-size:13px;line-height:1.4">
          <strong>${object.gemeente}</strong><br/>
          ${object.count.toLocaleString('nl-NL')} reizigers
        </div>`,
        style: { backgroundColor: '#fff', color: '#1a1a1a', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
      };
    }
    return {
      html: `<div style="padding:6px 10px;font-size:13px;line-height:1.4">
        <strong>${object.origin}</strong> → <strong>${object.destination}</strong><br/>
        Ritten: <strong>${Math.round(object.flow).toLocaleString('nl-NL')}</strong>
      </div>`,
      style: { backgroundColor: '#fff', color: '#1a1a1a', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
    };
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      {!fullscreen && <Sidebar activeItem="tools" user={user} />}

      {/* Left panel */}
      <div className={`w-80 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden ${fullscreen ? 'hidden' : ''}`}>
        <div className="p-4 border-b border-gray-100">
          <h1 className="text-lg font-semibold text-gray-900">Spinnenkaart</h1>
          <p className="text-xs text-gray-500 mt-0.5">ODIN herkomst-bestemmingslijnen</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Gemeente search */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Gemeente</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
              placeholder="Zoek gemeente..."
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            />
            {searchOpen && searchResults.length > 0 && (
              <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map((r) => (
                  <li
                    key={r.code}
                    onClick={() => handleSelectGemeente(r)}
                    className="px-3 py-1.5 text-sm cursor-pointer hover:bg-teal-50 hover:text-teal-700"
                  >
                    {r.name}
                  </li>
                ))}
              </ul>
            )}
            {selectedGemeente && (
              <p className="text-xs text-teal-600 mt-1">
                Geselecteerd: {selectedGemeente.name} ({selectedGemeente.code})
              </p>
            )}
          </div>

          {/* Mode */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Vervoerswijze</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            >
              {MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Motive */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Motief</label>
            <select
              value={motive}
              onChange={(e) => setMotive(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            >
              {MOTIVE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Top N slider */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Top verbindingen: <span className="font-semibold text-teal-600">{topN}</span>
            </label>
            <input
              type="range"
              min={5}
              max={100}
              step={1}
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              className="w-full accent-teal-600"
            />
            <div className="flex justify-between text-[10px] text-gray-400">
              <span>5</span>
              <span>100</span>
            </div>
          </div>

          {/* Include internal */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(e) => setIncludeInternal(e.target.checked)}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700">Interne verplaatsingen</span>
          </label>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!selectedGemeente || loading}
            className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Laden...' : 'Analyse starten'}
          </button>

          {/* Error */}
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-700">
              {error}
            </div>
          )}

          {/* Results table */}
          {spiderData && spiderData.features.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-800">
                  Resultaten ({spiderData.features.length})
                </h2>
                <span className="text-xs text-gray-500">
                  Totaal: {Math.round(totalFlow).toLocaleString('nl-NL')} ritten
                </span>
              </div>

              {/* Export buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleExportPNG}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Export PNG
                </button>
                <button
                  onClick={handleExportSVG}
                  className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Export SVG
                </button>
              </div>

              {/* Metadata */}
              {spiderData.metadata && (
                <div className="text-[11px] text-gray-500 space-y-0.5">
                  <p>Dataset: {spiderData.metadata.dataset}</p>
                  <p>Max flow: {Math.round(spiderData.metadata.max_flow).toLocaleString('nl-NL')}</p>
                </div>
              )}

              {/* Table */}
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600">
                      <th className="text-left px-2 py-1.5 font-medium">Herkomst</th>
                      <th className="text-left px-2 py-1.5 font-medium">Bestemming</th>
                      <th className="text-right px-2 py-1.5 font-medium">Ritten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spiderData.features
                      .slice()
                      .sort((a, b) => b.properties.flow - a.properties.flow)
                      .map((f, i) => (
                        <tr
                          key={i}
                          className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-2 py-1 text-gray-700 truncate max-w-[100px]" title={f.properties.origin}>
                            {f.properties.origin}
                          </td>
                          <td className="px-2 py-1 text-gray-700 truncate max-w-[100px]" title={f.properties.destination}>
                            {f.properties.destination}
                          </td>
                          <td className="px-2 py-1 text-right text-gray-900 font-medium tabular-nums">
                            {Math.round(f.properties.flow).toLocaleString('nl-NL')}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Demographics section */}
          {profileData && profileData.total_respondents > 0 && (
            <div className="space-y-3">
              <div className="border-t border-gray-200 pt-3">
                <h2 className="text-sm font-medium text-gray-800 mb-1">
                  Reizigers ({profileData.total_respondents.toLocaleString('nl-NL')})
                </h2>
                <p className="text-[11px] text-gray-500 mb-3">Demografisch profiel respondenten</p>
              </div>

              {/* Home locations toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showHomeLocations}
                  onChange={(e) => setShowHomeLocations(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Woonlocaties tonen</span>
              </label>

              {/* Gender */}
              <DemoBar label="Geslacht" data={profileData.demographics.sex} color="#068AA1" />

              {/* Age */}
              <DemoBar label="Leeftijd" data={profileData.demographics.age_groups} color="#51BA6D" />

              {/* Education */}
              <DemoBar label="Opleiding" data={profileData.demographics.education} color="#068AA1" />

              {/* Income */}
              <DemoBar label="Inkomen" data={profileData.demographics.income} color="#E62E73" />
            </div>
          )}

          {spiderData && spiderData.features.length === 0 && !error && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-700">
              Geen verbindingen gevonden voor deze selectie.
            </div>
          )}
        </div>
      </div>

      {/* Map panel */}
      <div className="flex-1 relative">
        <DeckGL
          ref={deckRef}
          viewState={viewState}
          onViewStateChange={({ viewState: vs }) => setViewState(vs as typeof viewState)}
          controller={true}
          layers={layers}
          getTooltip={getTooltip as any}
          useDevicePixels={true}
        />

        {/* Fullscreen toggle */}
        <button
          onClick={() => setFullscreen((f) => !f)}
          className="absolute top-4 right-4 z-10 bg-white/95 backdrop-blur rounded-lg shadow-md p-2 hover:bg-gray-50 transition-colors"
          title={fullscreen ? 'Volledig scherm sluiten' : 'Volledig scherm'}
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {fullscreen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            )}
          </svg>
        </button>

        {/* Legend overlay */}
        {arcs.length > 0 && (
          <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-md p-3 text-xs space-y-2 pointer-events-none">
            <p className="font-medium text-gray-800">Spinnenkaart — {selectedGemeente?.name}</p>
            <div className="flex items-center gap-2">
              <div className="h-0.5 w-8" style={{ background: 'linear-gradient(to right, #068AA1, #E62E73)' }} />
              <span className="text-gray-600">Herkomst → Bestemming</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                <div className="h-px w-4 bg-gray-400" />
                <div className="h-1 w-4 bg-gray-400" />
              </div>
              <span className="text-gray-600">Lijndikte ~ ritten</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Horizontal bar chart for a demographic distribution */
function DemoBar({ label, data, color }: { label: string; data: Record<string, number>; color: string }) {
  const entries = Object.entries(data).filter(([k]) => k !== 'Onbekend');
  if (entries.length === 0) return null;
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const maxVal = Math.max(...entries.map(([, v]) => v));

  return (
    <div>
      <span className="text-[11px] font-medium text-gray-600 block mb-1">{label}</span>
      <div className="space-y-1">
        {entries.map(([key, count]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 w-16 truncate text-right flex-shrink-0" title={key}>
              {key}
            </span>
            <div className="flex-1 h-3 bg-gray-100 rounded-sm overflow-hidden">
              <div
                className="h-full rounded-sm transition-all"
                style={{
                  width: `${(count / maxVal) * 100}%`,
                  backgroundColor: color,
                  opacity: 0.7 + (count / maxVal) * 0.3,
                }}
              />
            </div>
            <span className="text-[10px] text-gray-400 w-10 text-right tabular-nums">
              {total > 0 ? `${Math.round((count / total) * 100)}%` : '0%'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
